import {
  Conversation,
  ConversationAttributes,
  ConversationType,
  ConversationMetadata,
  PrivateConversationMetadata,
  GroupConversationMetadata,
  PublicConversationMetadata,
} from 'models/twilio';
import { toTwilioMessage } from 'models/twilio/converters';

const buildSpecificMetadata = async (
  conversation: Conversation,
  conversationType: ConversationType,
  baseMetadata: ConversationMetadata,
  selfUuid: string,
) => {
  let specifcMetadata = {} as ConversationMetadata
    | PrivateConversationMetadata
    | GroupConversationMetadata
    | PublicConversationMetadata;

  switch (conversationType) {
    case ConversationType.PRIVATE: {
      const participants = await conversation.getParticipants();
      const otherParicipants = participants.filter((participant) => participant.identity !== selfUuid);
      await Promise.all(otherParicipants.map((participant) => participant.getUser()));
      const otherUserId = otherParicipants?.[0]?.identity || null;
      specifcMetadata = { ...baseMetadata, otherUserId } as PrivateConversationMetadata;
      break;
    }
    case ConversationType.GROUP: {
      const conversationName = conversation.friendlyName ?? conversation.sid;
      specifcMetadata = {
        ...baseMetadata,
        conversationName,
        otherUserIds: [],
        fetchedAllParticipants: false,
      } as GroupConversationMetadata;
      break;
    }
    case ConversationType.PUBLIC: {
      specifcMetadata = {
        ...baseMetadata,
        otherUserIds: [],
        fetchedAllParticipants: false,
      } as PublicConversationMetadata;
      break;
    }
    default: specifcMetadata = baseMetadata;
  }

  return specifcMetadata;
};

const buildInitialMetadata = async (
  conversation: Conversation,
  selfUuid: string,
) => {
  const conversationType = (conversation.attributes as ConversationAttributes)?.type || ConversationType.UNKNOWN;
  const { items: messages } = await conversation.getMessages(1);
  const currentParticipant = await conversation.getParticipantByIdentity(selfUuid);

  const baseMetadata: ConversationMetadata = {
    id: conversation.sid,
    conversationType,
    hasLoadedMessages: false,
    lastMessage: messages.length ? toTwilioMessage(messages[0]) : null,
    lastReadMessageIndex: currentParticipant?.lastReadMessageIndex ?? -1,
    lastFetchedMessageIndex: null,
    dateCreated: conversation.dateCreated,
    isLoading: false,
    uniqueName: conversation.uniqueName,
  };

  const specificMetadata = await buildSpecificMetadata(
    conversation,
    conversationType,
    baseMetadata,
    selfUuid,
  );

  return specificMetadata as ConversationMetadata;
};

export default buildInitialMetadata;
